import { nextTick, watch, type Ref, type Reactive } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  CognitoUserCreateInput,
  ShopListInput,
  CreateCognitoUserMutation,
  UpdateCognitoUserMutation,
  UpdateCognitoUserPasswordMutation,
} from "@/generated/graphql";
import { routerPath } from "@/common/router";
import {
  localStorageGetAuthType,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { OPERATOR_LIST_FILE_NAME } from "@/views/admin/OperatorList/constants";
import {
  checkChainListDisplay,
  isOperatorGroupAdmin,
} from "@/common/business";
import { OperatorGroup } from "@/common/constant";
import operatorStore from "@/store/operatorStore";
import type { RadioItem, ChainItem, ShopItem } from "../types";

/** オペレータ登録 ボタン操作・画面遷移に必要な依存をまとめた型。 */
interface UseOperatorRegisterActionsDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  updateMode: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  createCognitoUserMutation: Ref<CreateCognitoUserMutation | null | undefined>;
  updateCognitoUserMutation: Ref<UpdateCognitoUserMutation | null | undefined>;
  updateCognitoUserPasswordMutation: Ref<UpdateCognitoUserPasswordMutation | null | undefined>;
  cognitUserCreateInput: Reactive<CognitoUserCreateInput>;
  shopListInput: Reactive<ShopListInput>;
  chainSelectValues: Ref<{ chain_code: string; name: string } | null | undefined>;
  shopSelectValues: Ref<{ shop_code: string; name: string } | null | undefined>;
  groupSelectValues: Ref<RadioItem>;
  personalSelectValues: Ref<RadioItem>;
  personalDlSelectValues: Ref<RadioItem>;
  authtypeSelectValues: Ref<RadioItem>;
  comfirm_temporary_password: Ref<string>;
  email_required: Ref<number>;
  tel_required: Ref<number>;
  isIpRestrictedChain: Ref<boolean>;
  update_mode_operator_email: Ref<string>;
  chainViewFlag: Ref<boolean>;
  wordList: Ref<Record<string, string>>;
  chainListItems: Ref<ChainItem[]>;
  shopListItems: Ref<ShopItem[]>;
  listEditFlag: Ref<boolean>;
  createCognitoUserData: (input: CognitoUserCreateInput) => Promise<CreateCognitoUserMutation | null | undefined>;
  updateCognitoUserData: (
    input: any,
    logInput: any
  ) => Promise<UpdateCognitoUserMutation | null | undefined>;
  updateCognitoUserPasswordData: (
    input: any,
    logInput: any
  ) => Promise<UpdateCognitoUserPasswordMutation | null | undefined>;
  getChainListData: (customerCode: string) => Promise<ChainItem[] | null | undefined>;
  getShopListData: (input: ShopListInput) => Promise<ShopItem[] | null | undefined>;
  setChainCode: (chainCode: string, selectUpdate: boolean) => Promise<void>;
  createGroupRadio: () => Promise<void>;
  onClickGroup: (index: string) => Promise<void>;
  allClear: () => Promise<void>;
  initialize: () => Promise<void>;
}

/**
 * オペレータ登録 ボタン操作・画面遷移を扱う composable。
 * 確認・登録・変更・キャンセル・完了後の遷移を管理する。
 */
export const useOperatorRegisterActions = (deps: UseOperatorRegisterActionsDeps) => {
  const {
    errdialog,
    errMessage,
    overlay,
    updateMode,
    displayConfirmDialog,
    displayCompletedDialog,
    createCognitoUserMutation,
    updateCognitoUserMutation,
    updateCognitoUserPasswordMutation,
    cognitUserCreateInput,
    shopListInput,
    chainSelectValues,
    shopSelectValues,
    groupSelectValues,
    personalSelectValues,
    personalDlSelectValues,
    authtypeSelectValues,
    comfirm_temporary_password,
    email_required,
    tel_required,
    isIpRestrictedChain,
    update_mode_operator_email,
    chainViewFlag,
    wordList,
    chainListItems,
    shopListItems,
    listEditFlag,
    createCognitoUserData,
    updateCognitoUserData,
    updateCognitoUserPasswordData,
    getChainListData,
    getShopListData,
    setChainCode,
    createGroupRadio,
    onClickGroup,
    allClear,
    initialize,
  } = deps;

  const router = useRouter();
  const route = useRoute();

  /**
   * 一覧へ戻る @Click
   */
  const onClickBack = async (): Promise<void> => {
    // 店舗登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
    await router.push({
      name: OPERATOR_LIST_FILE_NAME,
      query: { searchMode: "true" },
    });
  };

  /**
   * 各種設定
   */
  const createSetting = async (): Promise<void> => {
    if (groupSelectValues.value.index === OperatorGroup.other) {
      //運用管理者を選択
      await setChainCode(localStorageGetChainCode(), true);
    }
    //所属グループ
    cognitUserCreateInput.group = groupSelectValues.value.index;
    //店舗コード
    cognitUserCreateInput.shop_code = shopSelectValues.value
      ? shopSelectValues.value!.shop_code
      : "";
    //個人情報閲覧
    cognitUserCreateInput.personal_info = personalSelectValues.value.index;
    //個人情報ダウンロード
    cognitUserCreateInput.personal_info_dl = personalDlSelectValues.value.index;
    //権限
    cognitUserCreateInput.auth_type = authtypeSelectValues.value.index;
  };

  /**
   * 「登録手続きへ(確認)」ボタン @Click
   */
  const onClickOpenConfirmDialog = async (): Promise<void> => {
    //パスワードチェックする
    if (
      updateMode.value === false ||
      comfirm_temporary_password.value.length > 0
    ) {
      const regexPw = new RegExp(
        "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\\-_]).{8,128}$"
      );
      if (!regexPw.test(comfirm_temporary_password.value)) {
        errdialog.value = true;
        errMessage.value =
          "パスワードには少なくとも1つの半角英大文字、半角英小文字、英数字、記号(#?!@$%^&*-_)を含めてください。";
        return;
      }
    }

    // オペレータ登録確認ダイアログ 表示
    displayConfirmDialog.value = true;
  };

  /**
   * キャンセル @Click
   */
  const onClickConfirmCancel = async (): Promise<void> => {
    // オペレータ登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
  };

  /**
   * オペレータの登録する @Click
   */
  const onClickConfirmEntry = async (): Promise<void> => {
    overlay.value = true;
    try {
      //設定
      await createSetting();
      if (updateMode.value) {
        // オペレータ更新
        updateCognitoUserMutation.value = await updateCognitoUserData(
          {
            user_id: cognitUserCreateInput.user_id,
            customer_code: cognitUserCreateInput.customer_code,
            chain_code: cognitUserCreateInput.chain_code,
            shop_code: cognitUserCreateInput.shop_code,
            group: cognitUserCreateInput.group,
            email: cognitUserCreateInput.email ? cognitUserCreateInput.email : "",
            phone_number: cognitUserCreateInput.phone_number
              ? cognitUserCreateInput.phone_number
              : "",
            name: cognitUserCreateInput.name,
            auth_type: cognitUserCreateInput.auth_type,
            personal_info: cognitUserCreateInput.personal_info,
            personal_info_dl: cognitUserCreateInput.personal_info_dl,
            operator_column_email:
              cognitUserCreateInput.operator_column_email,
            operator_column_tel: tel_required.value,
          },
          {
            area_code: "10",
            detail: "情報更新",
            display_category: "オペレーター",
            operation: "オペレータ情報更新",
            user_id: localStorageGetUserId(),
          }
        );
        if (comfirm_temporary_password.value.length > 0) {
          // オペレータパスワード更新
          updateCognitoUserPasswordMutation.value =
            await updateCognitoUserPasswordData(
              {
                chain_code: cognitUserCreateInput.chain_code,
                customer_code: cognitUserCreateInput.customer_code,
                temporary: 1, //本パスワード
                user_id: cognitUserCreateInput.user_id,
                new_password: comfirm_temporary_password.value,
              },
              {
                area_code: "10",
                detail: "情報更新",
                display_category: "オペレーター",
                operation: "オペレータパスワード更新",
                user_id: localStorageGetUserId(),
              }
            );
        }
        await router.push({
          name: OPERATOR_LIST_FILE_NAME,
          query: { searchMode: "true" },
        });
      } else {
        // オペレータ登録
        createCognitoUserMutation.value = await createCognitoUserData(
          cognitUserCreateInput
        );
      }

      // オペレータ登録確認ダイアログ 非表示
      displayConfirmDialog.value = false;
    } catch (err: unknown) {
      overlay.value = false;
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }

      return;
    }

    await nextTick(async () => {
      // オペレータ登録完了ダイアログ 表示
      displayCompletedDialog.value = true;
      overlay.value = false;
    });
  };

  /**
   * 登録画面の初期状態に戻す
   */
  async function resetToRegisterView(): Promise<void> {
    updateMode.value = false;
    displayConfirmDialog.value = false;
    displayCompletedDialog.value = false;
    email_required.value = 0;
    tel_required.value = 0;
    isIpRestrictedChain.value = true;
    update_mode_operator_email.value = "";
    chainListItems.value = [];
    shopListItems.value = [];
    await operatorStore.setupValue({ user_id: "", chain_code: "" });

    //入力項目をクリア
    await allClear();

    // 初期化処理
    await initialize();

    //所属グループのラジオボタンを反映する
    await createGroupRadio();

    if (checkChainListDisplay(localStorageGetAuthType())) {
      // =============
      // 運用管理者
      // =============
      // チェーン情報を取得する
      chainListItems.value =
      (await getChainListData(localStorageGetCustomerCode())) ?? [];
      chainViewFlag.value = true;
    } else {
      // ===================
      // 運用管理者以外の場合
      // ===================
      // チェーンコードはログインユーザのチェーンコードにする
      await setChainCode(localStorageGetChainCode(), true);
      //チェーンリストは表示しない
      chainViewFlag.value = false;
      //店舗情報を取得する
      shopListItems.value = (await getShopListData(shopListInput)) ?? [];
    }

    //権限のラジオボタンを反映する
    onClickGroup(groupSelectValues.value.index);
  }

  /**
   * 戻る @Click
   */
  const onClickCompletedGoStart = async (): Promise<void> => {
    // オペレータ登録完了ダイアログ 非表示
    displayCompletedDialog.value = false;

    await nextTick(async () => {
      await resetToRegisterView();

      // 「オペレータ登録」画面へ遷移
      await router.push({ path: routerPath.operatorRegister });
    });
  };

  // 監視：一覧編集フラグ変更
  watch(
    () => listEditFlag.value,
    async (val, prev) => {
      displayConfirmDialog.value = false;
      if (!val && prev && route.name === "OperatorRegister" && updateMode.value) {
        await resetToRegisterView();
      }
    }
  );

  return {
    onClickBack,
    onClickOpenConfirmDialog,
    onClickConfirmCancel,
    onClickConfirmEntry,
    onClickCompletedGoStart,
    resetToRegisterView,
  };
};
